import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, User, ArrowLeft, Share2, BookOpen, Clock, Phone } from 'lucide-react';

const ArticoloDettaglio = () => {
  const { slug } = useParams();
  const [articolo, setArticolo] = useState(null);
  const [articoliCorrelati, setArticoliCorrelati] = useState([]);
  const [loading, setLoading] = useState(true);

  // Articoli statici di esempio
  const articoliStatici = {
    "prevenzione-mal-di-schiena": {
      id: 1,
      titolo: "Prevenzione del Mal di Schiena",
      slug: "prevenzione-mal-di-schiena",
      riassunto: "Consigli pratici per prevenire il mal di schiena nella vita quotidiana",
      contenuto: `
        <h2>Introduzione</h2>
        <p>Il mal di schiena è una delle problematiche più comuni nella popolazione adulta. Spesso causato da posture scorrette, stress e mancanza di movimento, può essere prevenuto con semplici accorgimenti quotidiani.</p>
        
        <h2>Cause Principali</h2>
        <ul>
          <li>Posture scorrette prolungate</li>
          <li>Sedentarietà</li>
          <li>Stress e tensione muscolare</li>
          <li>Movimenti bruschi o carichi eccessivi</li>
          <li>Debolezza della muscolatura del core</li>
        </ul>
        
        <h2>Consigli per la Prevenzione</h2>
        <h3>1. Mantenere una Postura Corretta</h3>
        <p>Sia da seduti che in piedi, è importante mantenere la colonna vertebrale in posizione neutra. Evitare di stare curvi sulla scrivania o di assumere posizioni asimmetriche per periodi prolungati.</p>
        
        <h3>2. Esercizio Fisico Regolare</h3>
        <p>Un'attività fisica costante aiuta a mantenere la muscolatura forte e flessibile. Particolarmente utili sono gli esercizi per il core e lo stretching.</p>
        
        <h3>3. Ergonomia del Posto di Lavoro</h3>
        <p>Regolare l'altezza della sedia, del monitor e della scrivania per mantenere una postura naturale durante il lavoro.</p>
        
        <h2>Quando Consultare un Professionista</h2>
        <p>Se il dolore persiste per più di qualche giorno o si presenta frequentemente, è consigliabile consultare un fisioterapista per una valutazione approfondita.</p>
      `,
      data_pubblicazione: "2024-12-01",
      tempo_lettura: 5,
      immagine_copertina: "/images/fisioterapia_centro_1.jpeg",
      categoria: { nome: "Prevenzione", colore: "blue" },
      autore: "Dott. Peppino Salvatore Papa",
      visualizzazioni: 245
    },
    "esercizi-posturali-bambini": {
      id: 2,
      titolo: "Esercizi Posturali per Bambini",
      slug: "esercizi-posturali-bambini",
      riassunto: "Guida agli esercizi posturali specifici per l'età evolutiva",
      contenuto: `
        <h2>L'Importanza della Postura nei Bambini</h2>
        <p>Durante l'età evolutiva, è fondamentale prestare attenzione alla postura per prevenire problematiche future e favorire uno sviluppo armonico.</p>
        
        <h2>Esercizi Consigliati</h2>
        <h3>1. Esercizi di Allungamento</h3>
        <p>Semplici movimenti di stretching per mantenere la flessibilità della colonna vertebrale.</p>
        
        <h3>2. Rinforzo del Core</h3>
        <p>Esercizi divertenti e adatti all'età per rafforzare la muscolatura addominale e dorsale.</p>
        
        <h3>3. Giochi di Equilibrio</h3>
        <p>Attività ludiche che migliorano la propriocezione e il controllo posturale.</p>
        
        <h2>Consigli per i Genitori</h2>
        <p>È importante creare un ambiente che favorisca una postura corretta, con mobili adeguati all'altezza del bambino e pause frequenti durante lo studio.</p>
      `,
      data_pubblicazione: "2024-11-28",
      tempo_lettura: 7,
      immagine_copertina: "/images/terapia_neonatale_1.jpeg",
      categoria: { nome: "Pediatria", colore: "green" },
      autore: "Dott. Peppino Salvatore Papa",
      visualizzazioni: 189
    },
    "riabilitazione-post-chirurgica": {
      id: 3,
      titolo: "Riabilitazione Post-Chirurgica",
      slug: "riabilitazione-post-chirurgica",
      riassunto: "Protocolli e tempistiche per una riabilitazione efficace dopo interventi chirurgici ortopedici",
      contenuto: `
        <h2>Introduzione alla Riabilitazione Post-Chirurgica</h2>
        <p>La riabilitazione dopo un intervento chirurgico è fondamentale per il recupero completo della funzionalità e per prevenire complicazioni.</p>
        
        <h2>Fasi della Riabilitazione</h2>
        <h3>Fase Acuta (0-2 settimane)</h3>
        <p>Controllo del dolore e dell'infiammazione, mobilizzazione precoce e protezione della zona operata.</p>
        
        <h3>Fase Subacuta (2-6 settimane)</h3>
        <p>Recupero graduale del movimento e inizio del rinforzo muscolare controllato.</p>
        
        <h3>Fase di Recupero Funzionale (6+ settimane)</h3>
        <p>Ritorno alle attività quotidiane e sportive con programmi specifici.</p>
        
        <h2>Protocolli Specifici</h2>
        <p>Ogni intervento richiede un protocollo riabilitativo specifico basato sul tipo di chirurgia e sulle caratteristiche del paziente.</p>
      `,
      data_pubblicazione: "2024-11-25",
      tempo_lettura: 8,
      immagine_copertina: "/images/fisioterapia_centro_2.webp",
      categoria: { nome: "Riabilitazione", colore: "purple" },
      autore: "Dott. Peppino Salvatore Papa",
      visualizzazioni: 156
    },
    "plagiocefalie-prevenzione-trattamento": {
      id: 4,
      titolo: "Plagiocefalie: Prevenzione e Trattamento",
      slug: "plagiocefalie-prevenzione-trattamento",
      riassunto: "Tutto quello che i genitori devono sapere sulle deformità craniche posizionali nei neonati",
      contenuto: `
        <h2>Cosa sono le Plagiocefalie</h2>
        <p>Le plagiocefalie sono deformità craniche posizionali che si sviluppano nei primi mesi di vita del neonato.</p>
        
        <h2>Cause Principali</h2>
        <ul>
          <li>Posizione prolungata sulla schiena</li>
          <li>Torcicollo miogeno congenito</li>
          <li>Posizioni preferenziali del capo</li>
          <li>Parto difficoltoso</li>
        </ul>
        
        <h2>Prevenzione</h2>
        <h3>Tummy Time</h3>
        <p>Posizionare il bambino a pancia in giù durante la veglia sotto supervisione.</p>
        
        <h3>Variazione delle Posizioni</h3>
        <p>Alternare la posizione del capo durante il sonno e l'allattamento.</p>
        
        <h2>Trattamento</h2>
        <p>Il trattamento precoce con terapia manuale e posizionamento è molto efficace nei primi mesi di vita.</p>
      `,
      data_pubblicazione: "2024-11-20",
      tempo_lettura: 6,
      immagine_copertina: "/images/plagiocefalia_trattamento_1.jpeg",
      categoria: { nome: "Neonatale", colore: "pink" },
      autore: "Dott. Peppino Salvatore Papa",
      visualizzazioni: 298
    },
    "torcicollo-miogeno-riconoscimento": {
      id: 5,
      titolo: "Torcicollo Miogeno: Riconoscimento Precoce",
      slug: "torcicollo-miogeno-riconoscimento",
      riassunto: "Come riconoscere i segni del torcicollo miogeno congenito e l'importanza dell'intervento precoce",
      contenuto: `
        <h2>Il Torcicollo Miogeno Congenito</h2>
        <p>Il torcicollo miogeno è una condizione che causa l'inclinazione e rotazione del capo del neonato.</p>
        
        <h2>Segni da Riconoscere</h2>
        <ul>
          <li>Inclinazione costante del capo da un lato</li>
          <li>Difficoltà a girare il capo</li>
          <li>Massa palpabile nel muscolo sternocleidomastoideo</li>
          <li>Asimmetria facciale</li>
        </ul>
        
        <h2>Importanza dell'Intervento Precoce</h2>
        <p>Il trattamento nei primi mesi di vita ha un'efficacia molto elevata e previene complicazioni future.</p>
        
        <h2>Trattamento</h2>
        <h3>Stretching Specifico</h3>
        <p>Esercizi di allungamento del muscolo sternocleidomastoideo.</p>
        
        <h3>Posizionamento</h3>
        <p>Tecniche di posizionamento per favorire il movimento nella direzione limitata.</p>
      `,
      data_pubblicazione: "2024-11-15",
      tempo_lettura: 5,
      immagine_copertina: "/images/terapia_neonatale_2.jpeg",
      categoria: { nome: "Neonatale", colore: "pink" },
      autore: "Dott. Peppino Salvatore Papa",
      visualizzazioni: 167
    },
    "recupero-sportivo-metodologie": {
      id: 6,
      titolo: "Recupero Sportivo: Metodologie Avanzate",
      slug: "recupero-sportivo-metodologie",
      riassunto: "Tecniche moderne per il recupero degli atleti e la prevenzione degli infortuni sportivi",
      contenuto: `
        <h2>Il Recupero nell'Atleta Moderno</h2>
        <p>Il recupero sportivo richiede un approccio multidisciplinare e tecnologie avanzate.</p>
        
        <h2>Metodologie di Recupero</h2>
        <h3>Terapia Manuale Sportiva</h3>
        <p>Tecniche specifiche per il trattamento delle problematiche dell'atleta.</p>
        
        <h3>Tecnologie Avanzate</h3>
        <ul>
          <li>Tecar terapia</li>
          <li>Onde d'urto</li>
          <li>Laser terapia ad alta potenza</li>
          <li>Crioterapia</li>
        </ul>
        
        <h2>Prevenzione degli Infortuni</h2>
        <p>Programmi specifici di preparazione fisica e correzione degli squilibri muscolari.</p>
        
        <h2>Ritorno all'Attività</h2>
        <p>Protocolli graduali per il ritorno sicuro alla pratica sportiva.</p>
      `,
      data_pubblicazione: "2024-11-10",
      tempo_lettura: 9,
      immagine_copertina: "/images/fitness_trainer_1.jpeg",
      categoria: { nome: "Sport", colore: "orange" },
      autore: "Dott. Peppino Salvatore Papa",
      visualizzazioni: 134
    }
  };

  useEffect(() => {
    const loadArticolo = () => {
      const articoloTrovato = articoliStatici[slug];
      if (articoloTrovato) {
        setArticolo(articoloTrovato);
        // Simula articoli correlati
        const altriArticoli = Object.values(articoliStatici).filter(a => a.slug !== slug);
        setArticoliCorrelati(altriArticoli.slice(0, 2));
      }
      setLoading(false);
    };

    loadArticolo();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container-custom py-20 text-center">
          <p className="text-muted-foreground">Caricamento articolo...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!articolo) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container-custom py-20 text-center space-y-4">
          <h1 className="text-2xl font-bold">Articolo non trovato</h1>
          <p className="text-muted-foreground">L'articolo che stai cercando non esiste.</p>
          <Button asChild>
            <Link to="/articoli">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Torna agli Articoli
            </Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header Articolo */}
      <section className="section-padding bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Button variant="outline" asChild>
                <Link to="/articoli">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Torna agli Articoli
                </Link>
              </Button>
            </div>
            
            <div className="space-y-6">
              <Badge className={`bg-primary/90 text-white`}>
                {articolo.categoria.nome}
              </Badge>
              
              <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
                {articolo.titolo}
              </h1>
              
              <p className="text-xl text-muted-foreground">
                {articolo.riassunto}
              </p>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>{articolo.autore}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(articolo.data_pubblicazione)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>{articolo.tempo_lettura} min di lettura</span>
                </div>
                <div className="flex items-center space-x-2">
                  <BookOpen className="h-4 w-4" />
                  <span>{articolo.visualizzazioni} visualizzazioni</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Immagine Copertina */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <img 
              src={articolo.immagine_copertina} 
              alt={articolo.titolo}
              className="w-full h-64 md:h-96 object-cover rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Contenuto Articolo */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <Card className="medical-card">
              <CardContent className="p-8">
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: articolo.contenuto }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Articoli Correlati */}
      {articoliCorrelati.length > 0 && (
        <section className="section-padding bg-muted/30">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8">Articoli Correlati</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {articoliCorrelati.map((articoloCorrelato) => (
                  <Card key={articoloCorrelato.id} className="medical-card hover:scale-105 smooth-transition">
                    <div className="relative overflow-hidden rounded-t-xl">
                      <img 
                        src={articoloCorrelato.immagine_copertina} 
                        alt={articoloCorrelato.titolo}
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-primary/90 text-white text-xs">
                          {articoloCorrelato.categoria.nome}
                        </Badge>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{articoloCorrelato.titolo}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {articoloCorrelato.riassunto}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to={`/articoli/${articoloCorrelato.slug}`}>
                          Leggi Articolo
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-padding medical-gradient text-white">
        <div className="container-custom text-center space-y-8">
          <h2 className="text-4xl font-bold">Hai Bisogno di Aiuto?</h2>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Contattaci per una consulenza personalizzata sui temi trattati in questo articolo
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary" 
              className="bg-white text-primary hover:bg-white/90"
              onClick={() => window.open('https://www.sumupbookings.com/dott-papa-peppino-salvatore', '_blank')}
            >
              <Calendar className="mr-2 h-4 w-4" />
              Prenota Consulenza
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" asChild>
              <a href="tel:+393475391433">
                <Phone className="mr-2 h-4 w-4" />
                Chiama: +39 347 5391433
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticoloDettaglio;